import React, { useEffect, useState } from "react";
import { Plus, Trash, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AdminModal, BTN_BLACK, BTN_WHITE, INPUT, errText } from "./AdminModal";

const BOX = "w-full border-2 border-black bg-white px-2 py-1 text-sm";

function Contacts({ rows, setRows }) {
  const set = (i, patch) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div className="mb-4" data-testid="spec-email-contacts">
      <div className="mb-1 flex items-center gap-2">
        <span className="label-mono">Tech contacts (up to 3)</span>
        <button type="button" disabled={rows.length >= 3} onClick={() => setRows([...rows, { name: "", email: "" }])}
                className="ml-auto inline-flex items-center gap-1 border-2 border-black bg-white px-2 py-1 text-[10px] uppercase tracking-widest transition-colors duration-100 hover:bg-black hover:text-white disabled:opacity-40"
                data-testid="spec-email-contact-add">
          <Plus size={11} weight="bold" /> Add a contact
        </button>
      </div>
      {rows.length === 0 && <p className="text-[11px] text-zinc-600">Add who should get it — saved on this buyer for next time.</p>}
      {rows.map((r, i) => (
        <div key={i} className="mb-2 grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_auto]" data-testid={`spec-email-contact-${i}`}>
          <input value={r.name} onChange={(e) => set(i, { name: e.target.value })} placeholder="Name (optional)" maxLength={80}
                 className={BOX} data-testid={`spec-email-contact-name-${i}`} />
          <input type="email" value={r.email} onChange={(e) => set(i, { email: e.target.value })} placeholder="tech@theirfirm.com"
                 className={BOX} data-testid={`spec-email-contact-email-${i}`} />
          <button type="button" onClick={() => setRows(rows.filter((_, j) => j !== i))} aria-label="Remove contact"
                  className="justify-self-start border-2 border-black bg-white px-2 py-1 transition-colors duration-100 hover:bg-red-600 hover:text-white"
                  data-testid={`spec-email-contact-remove-${i}`}>
            <Trash size={12} />
          </button>
        </div>
      ))}
    </div>
  );
}

// Email the spec sheet / field list (built from the window's current settings) to the buyer's tech contacts.
export function EmailSpecDialog({ body, destId, onClose }) {
  const [info, setInfo] = useState(null);
  const [rows, setRows] = useState([]);
  const [opts, setOpts] = useState({ files: ["pdf", "csv"], source: "sample", days: 7, note: "", copy_me: true });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/admin/lead-share/destinations/${destId}/tech-contacts`)
      .then((r) => { setInfo(r.data); setRows(r.data.contacts.length ? r.data.contacts : [{ name: "", email: "" }]); })
      .catch((e) => { toast.error(errText(e, "Couldn't load tech contacts")); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destId]);
  const toggleFile = (k) => setOpts((o) => ({ ...o, files: o.files.includes(k) ? o.files.filter((x) => x !== k) : [...o.files, k] }));
  const send = async () => {
    const bad = rows.findIndex((r) => r.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(r.email.trim()));
    if (bad >= 0) {
      toast.error(`Contact ${bad + 1}: check the email address`);
      return;
    }
    setBusy(true);
    try {
      const contacts = rows.filter((r) => r.email.trim()).map((r) => ({ name: r.name, email: r.email.trim() }));
      await api.put(`/admin/lead-share/destinations/${destId}/tech-contacts`, { contacts });
      const r = await api.post(`/admin/lead-share/destinations/${destId}/email-spec`, { ...body, ...opts });
      const until = new Date(r.data.expires_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
      toast.success(`Sent to ${r.data.sent_to.join(", ")} — links work until ${until}`);
      if (r.data.failed.length) toast.error(`Couldn't reach ${r.data.failed.join(", ")}`);
      onClose();
    } catch (e) {
      toast.error(errText(e, "Couldn't send the email"));
    } finally {
      setBusy(false);
    }
  };
  const footer = (
    <>
      <button type="button" className={BTN_WHITE} onClick={onClose} data-testid="spec-email-cancel">Cancel</button>
      <button type="button" className={BTN_BLACK} onClick={send} disabled={!info || busy || !opts.files.length || !rows.some((r) => r.email.trim())}
              data-testid="spec-email-send">{busy ? "Sending…" : "Send email"}</button>
    </>
  );
  return (
    <AdminModal eyebrow="Email the spec sheet" title={info ? `To ${info.buyer}'s tech team` : "Loading…"} onClose={onClose} footer={footer} testId="spec-email-modal">
      {!info ? <div className="label-mono py-4">Loading…</div> : (
        <>
          <p className="mb-3 text-[11px] text-zinc-600">
            They get a short email with secure links to the files below, {body?.use_saved ? "built from the fields this buyer gets right now: setup steps, how to reply, test leads and an example lead." : "built from what this window shows right now (saved or not)."}
          </p>
          <Contacts rows={rows} setRows={setRows} />
          <div className="mb-3 grid gap-3 sm:grid-cols-3">
            <div>
              <span className="label-mono">Files</span>
              {[["pdf", "Spec sheet (PDF)"], ["csv", "Field list (CSV)"]].map(([k, lbl]) => (
                <label key={k} className="mt-1 flex items-center gap-2 text-sm">
                  <input type="checkbox" className="accent-black" checked={opts.files.includes(k)} onChange={() => toggleFile(k)} data-testid={`spec-email-file-${k}`} />
                  {lbl}
                </label>
              ))}
            </div>
            <label className="block text-[11px]">
              <span className="label-mono">Example lead</span>
              <select value={opts.source} onChange={(e) => setOpts({ ...opts, source: e.target.value })} className={`${BOX} mt-1`} data-testid="spec-email-source">
                <option value="sample">Made-up sample lead</option>
                <option value="real">Most recent real lead sent to them</option>
              </select>
            </label>
            <label className="block text-[11px]">
              <span className="label-mono">Links work for</span>
              <select value={opts.days} onChange={(e) => setOpts({ ...opts, days: Number(e.target.value) })} className={`${BOX} mt-1`} data-testid="spec-email-days">
                <option value={7}>7 days</option>
                <option value={30}>30 days</option>
              </select>
            </label>
          </div>
          {opts.source === "real" && (
            <p className="mb-3 flex gap-1 text-[11px] text-red-700" data-testid="spec-email-real-warning">
              <Warning size={13} className="shrink-0" weight="bold" /> The files will include a real person&apos;s details. Only send them to this buyer&apos;s own team.
            </p>
          )}
          <label className="block text-[11px]">
            <span className="label-mono">Short note (optional)</span>
            <textarea value={opts.note} onChange={(e) => setOpts({ ...opts, note: e.target.value })} maxLength={1000} rows={3}
                      placeholder="e.g. We plan to start sending leads next Monday — let us know if anything needs changing."
                      className={INPUT} data-testid="spec-email-note" />
            <span className="text-zinc-500">{opts.note.length}/1000 · no web addresses or links</span>
          </label>
          <label className="mt-3 flex items-center gap-2 text-sm">
            <input type="checkbox" className="accent-black" checked={opts.copy_me} onChange={(e) => setOpts({ ...opts, copy_me: e.target.checked })} data-testid="spec-email-copy-me" />
            Send me a copy
          </label>
          <p className="mt-1 text-[11px] text-zinc-600" data-testid="spec-email-reply-to">Their replies go to <b>{info.reply_to}</b>.</p>
        </>
      )}
    </AdminModal>
  );
}
